"use client";

import { Link } from "@tanstack/react-router";
import { BadgeCheck, ChevronDown, LogOut } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSession } from "@/lib/session";
import { cn } from "@/lib/utils";

function colorForUser(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  }
  return `hsl(${hash % 360} 62% 45%)`;
}

function getInitials(name: string | undefined, email: string | undefined) {
  const source = name?.trim() || email?.trim();
  if (!source) {
    return "U";
  }

  const cleaned = source.replace(/[@._-]/g, " ");
  const letters = cleaned
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase())
    .filter(Boolean)
    .slice(0, 2)
    .join("");

  return letters || "U";
}

export function NavUser({
  compact = false,
  showName = false,
}: {
  compact?: boolean;
  showName?: boolean;
} = {}) {
  const { user, signOut } = useSession();

  if (!user) {
    return null;
  }

  const { email } = user;
  const displayName = user.name || email || "User";
  const initials = getInitials(user.name || undefined, email || undefined);
  const avatarColor = colorForUser(user.id);

  return (
    <div className="w-full">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            aria-label={`User menu for ${displayName}`}
            className={cn(
              "flex h-9 min-w-0 items-center gap-2 bg-transparent",
              showName
                ? "w-full justify-start px-2 text-left"
                : "p-0 hover:bg-transparent",
            )}
          >
            <Avatar className="size-6">
              <AvatarImage src={user.image ?? undefined} alt={displayName} />
              <AvatarFallback
                className="text-[11px] font-medium text-white"
                style={{ backgroundColor: avatarColor }}
              >
                {initials}
              </AvatarFallback>
            </Avatar>
            {showName ? (
              <span className="min-w-0 flex-1 truncate text-xs">
                {displayName}
              </span>
            ) : null}
            {!compact ? (
              <ChevronDown
                className={cn("text-muted-foreground", showName && "ml-auto")}
              />
            ) : null}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="min-w-56 rounded-lg"
          align="end"
          side={compact ? "right" : showName ? "top" : "bottom"}
          sideOffset={4}
        >
          <DropdownMenuLabel className="p-0 font-normal">
            <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
              <Avatar className="size-8">
                <AvatarImage src={user.image ?? undefined} alt={displayName} />
                <AvatarFallback
                  className="font-medium text-white"
                  style={{ backgroundColor: avatarColor }}
                >
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">{displayName}</span>
                {email ? (
                  <span className="truncate text-xs">{email}</span>
                ) : null}
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem asChild>
              <Link to="/account">
                <BadgeCheck className="mr-2 h-4 w-4" />
                Profile
              </Link>
            </DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => void signOut()}>
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
